import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { instructorActions } from '../actions/instructor.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class InstructorEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getInstructor$ = createEffect(() => {
        return this.actions.pipe(
            ofType(instructorActions.getInstructor),
            mergeMap(({ id }) => {
                return this.httpService.getInstructor(id).pipe(
                    finalizeLoader('instructor-get'),
                    map((instructor) => instructorActions.getInstructorSuccess({ payload: instructor })),
                    catchError((response: HttpErrorResponse) => of(instructorActions.getInstructorError({ code: response.status }))),
                );
            }),
        );
    });

    createInstructor$ = createEffect(() => {
        return this.actions.pipe(
            ofType(instructorActions.createInstructor),
            mergeMap(({ value }) => {
                return this.httpService.createInstructor(value).pipe(
                    finalizeLoader('instructor-create'),
                    map((instructor) => instructorActions.createInstructorSuccess({ payload: instructor })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.instructors.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.instructors.createError');
                        return of(instructorActions.createInstructorError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateInstructor$ = createEffect(() => {
        return this.actions.pipe(
            ofType(instructorActions.updateInstructor),
            mergeMap(({ id, value }) => {
                return this.httpService.updateInstructor(id, value).pipe(
                    finalizeLoader('instructor-update'),
                    map((instructor) => instructorActions.updateInstructorSuccess({ payload: instructor })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.instructors.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.instructors.updateError');
                        return of(instructorActions.updateInstructorError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteInstructor$ = createEffect(() => {
        return this.actions.pipe(
            ofType(instructorActions.deleteInstructor),
            mergeMap(({ id }) => {
                return this.httpService.deleteInstructor(id).pipe(
                    finalizeLoader('instructor-delete'),
                    map(() => instructorActions.deleteInstructorSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.instructors.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.instructors.deleteError');
                        return of(instructorActions.deleteInstructorError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
